import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { SessionExpiredModal } from "./components/SessionExpiredModal";
import { useSessionExpiredStore } from "./stores/sessionExpiredStore";
import { LandingPage } from "./pages/LandingPage";
import { LoginPage } from "./pages/LoginPage";
import SignUpPage from "./pages/SignUpPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";
import { MFAVerifyPage } from "./pages/MFAVerifyPage";
import { ProfilePage } from "./pages/ProfilePage";
import { PublicLayout } from "./components/layout/PublicLayout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { PortfolioPageNew as PortfolioPage } from "./pages/PortfolioPageNew";
import { SearchPage } from "./pages/SearchPage";
import { BacktestPage } from "./pages/BacktestPage";
import { BookmarksPage } from "./pages/BookmarksPage";
import { SettingsPage } from "./pages/SettingsPage";
import { TermsPage } from "./pages/TermsPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { BrokerageAccountsPage } from "./pages/BrokerageAccountsPage";
import { BrokeragePositionsPage } from "./pages/BrokeragePositionsPage";
import { BrokerageOrderHistoryPage } from "./pages/BrokerageOrderHistoryPage";
import { ExecutionPage } from "./pages/ExecutionPage";
import { AdminActivityLogsPage } from "./pages/AdminActivityLogsPage";
import { AdminUsersPage } from "./pages/AdminUsersPage";
import { AdminSubscriptionsPage } from "./pages/AdminSubscriptionsPage";
import { SupportPage } from "./pages/SupportPage";
import { FAQPage } from "./pages/FAQPage";
import { ExecutionReportPage } from "./pages/ExecutionReportPage";
import { SubscriptionPage } from "./pages/SubscriptionPage";
import StrategyBuilderPage from "./pages/StrategyBuilderPage";
import { DiversifierPage } from "./pages/DiversifierPage";
import { PortfolioLabPage } from "./pages/PortfolioLabPage";
import { IntroPage } from "./pages/IntroPage";
import { ProductIntroPage } from "./pages/ProductIntroPage";
import OptimizerJobsPage from "./pages/OptimizerJobsPage";
import OptimizerResultsPage from "./pages/OptimizerResultsPage";
import { MirrorLinksPage } from "./pages/MirrorLinksPage";
import { MirrorDashboardPage } from "./pages/MirrorDashboardPage";
import { PulsePage } from "./pages/PulsePage";
import { VideoCatalogPage } from "./pages/VideoCatalogPage";
import { useFeatureFlagStore } from "./stores/featureFlagStore";

function App() {
  const { isExpired, message, hideModal } = useSessionExpiredStore();
  const flags = useFeatureFlagStore((s) => s.flags);
  const loaded = useFeatureFlagStore((s) => s.loaded);
  const fetchFlags = useFeatureFlagStore((s) => s.fetchFlags);

  // Fetch feature flags on mount
  React.useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  // Don't render routes until feature flags are loaded (prevents catch-all redirect)
  if (!loaded) {
    return null;
  }
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* Landing page */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/intro" element={<IntroPage />} />
          <Route path="/product" element={<ProductIntroPage />} />
          
          {/* Auth routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/mfa-verify" element={<MFAVerifyPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          {/* All app routes use PublicLayout */}
          <Route element={<PublicLayout />}>
            {/* Public routes */}
            <Route path="/pulse" element={<PulsePage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/backtest" element={<BacktestPage />} />
            <Route path="/backtest/:strategyId" element={<BacktestPage />} />
            <Route path="/diversifier" element={<DiversifierPage />} />
            <Route
              path="/portfolio-lab"
              element={
                <ProtectedRoute feature="portfolio-lab">
                  <PortfolioLabPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/builder"
              element={
                <ProtectedRoute feature="builder">
                  <StrategyBuilderPage />
                </ProtectedRoute>
              }
            />

            {/* Optimizer routes — hidden when flags.OPTIMIZER is false */}
            {flags.OPTIMIZER && (
              <>
                <Route
                  path="/optimizer/jobs"
                  element={
                    <ProtectedRoute feature="optimizer">
                      <OptimizerJobsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/optimizer/jobs/:jobId"
                  element={
                    <ProtectedRoute feature="optimizer">
                      <OptimizerResultsPage />
                    </ProtectedRoute>
                  }
                />
              </>
            )}

            {/* Video catalog — hidden when flags.VIDEO_CATALOG is false */}
            {flags.VIDEO_CATALOG && (
              <>
                <Route path="/walkthroughs" element={<VideoCatalogPage />} />
                <Route path="/walkthroughs/:videoId" element={<VideoCatalogPage />} />
              </>
            )}

            {/* Protected routes - show auth page if not logged in */}
            <Route
              path="/portfolio"
              element={
                <ProtectedRoute feature="portfolio">
                  <PortfolioPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookmarks"
              element={
                <ProtectedRoute feature="bookmarks">
                  <BookmarksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute feature="settings">
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/brokerage"
              element={
                <ProtectedRoute feature="brokerage">
                  <BrokerageAccountsPage />
                </ProtectedRoute>
              }
            />
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route
              path="/brokerage/:accountId/positions"
              element={
                <ProtectedRoute feature="brokerage">
                  <BrokeragePositionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/brokerage/:accountId/orders"
              element={
                <ProtectedRoute feature="brokerage">
                  <BrokerageOrderHistoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/brokerage/:accountId/mirror"
              element={
                <ProtectedRoute feature="brokerage">
                  <MirrorLinksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/brokerage/:accountId/mirror/:linkId"
              element={
                <ProtectedRoute feature="brokerage">
                  <MirrorDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/execution/:investmentId"
              element={
                <ProtectedRoute feature="execution">
                  <ExecutionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/activity-logs"
              element={
                <ProtectedRoute feature="admin">
                  <AdminActivityLogsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute feature="admin">
                  <AdminUsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users/:userId"
              element={
                <ProtectedRoute feature="admin">
                  <AdminUsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/subscriptions"
              element={
                <ProtectedRoute feature="admin">
                  <AdminSubscriptionsPage />
                </ProtectedRoute>
              }
            />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route
              path="/admin/scheduler/runs/:runId/report"
              element={
                <ProtectedRoute feature="admin">
                  <ExecutionReportPage />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        
        {/* Global Session Expired Modal */}
        <SessionExpiredModal
          isOpen={isExpired}
          onClose={hideModal}
          message={message}
        />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
